import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {AsyncPipe} from '@angular/common';
import {combineLatest, filter, map, Observable} from 'rxjs';
import {Store} from '@ngrx/store';
import {selectBuilding, selectUser} from '../../state/selectors';
import {filterNulls} from '@brunata-angular/util';

@Component({
    selector: 'app-iframe',
    styles: [
        `
            iframe {
                flex-grow: 1;
                border: none;
            }
        `,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe],
    template: `
        @if (safeUrl$ | async; as safeUrl) {
            <iframe [src]="safeUrl"></iframe>
        }
    `,
})
export class IframeComponent {
    private readonly store = inject(Store);
    private readonly route = inject(ActivatedRoute);
    private readonly sanitizer = inject(DomSanitizer);

    private readonly buildingBasicDataId$: Observable<string | null> = this.store
        .select(selectBuilding)
        .pipe(
            map(state =>
                state.status === 'success'
                    ? state.buildingDetails.building.buildingBasicDataId
                    : null,
            ),
        );

    private readonly language$: Observable<string | null> = this.store.select(selectUser).pipe(
        filter(state => state.status === 'success'),
        map(state => state.user.language),
    );

    private readonly routeData$: Observable<string> = this.route.data.pipe(
        map(data => (typeof data['url'] === 'string' ? data['url'] : null)),
        filterNulls(),
    );

    readonly safeUrl$: Observable<SafeResourceUrl> = combineLatest([
        this.routeData$,
        this.buildingBasicDataId$,
        this.language$,
    ]).pipe(
        map(([routeData, buildingBasicDataId, language]) => {
            const baseUrl = routeData.replace('{buildingbasicdataid}', buildingBasicDataId ?? '');

            const url = new URL(baseUrl, window.location.origin);

            if (language !== null) {
                url.searchParams.set('lang', language);
            }
            return this.sanitizer.bypassSecurityTrustResourceUrl(url.toString());
        }),
    );
}
